// Stage helpers for dynamic modification rules: presets and conversions between
// the API body and the form.
import { STAGE_KIND } from 'lib/dynamicModificationStages';

export const STAGE_KIND_OPTIONS = [
  { value: STAGE_KIND.INSPECT, label: 'Ask the question' },
  { value: STAGE_KIND.SKIP, label: 'Skip the question' },
];

// A skip's time limit and a rule's reset period are counted in calendar days,
// months or years.
export const DEFAULT_PERIOD_UNIT = 'day';
const PERIOD_UNITS = ['day', 'month', 'year'];

/** The unit choices, read as "1 month" or "3 months" for the amount typed next to them. */
export const periodUnitOptions = (amount) =>
  PERIOD_UNITS.map((unit) => ({
    value: unit,
    label: Number(amount) === 1 ? unit : `${unit}s`,
  }));

let stageKey = 0;
const nextKey = () => `stage-${(stageKey += 1)}`;

export const makeStage = (overrides = {}) => ({
  key: nextKey(),
  stageNo: '',
  name: '',
  kind: STAGE_KIND.INSPECT,
  isInitial: false,
  levelShift: 0,
  defectShift: 0,
  okCount: '',
  okNextStageNo: null,
  failCount: '',
  failNextStageNo: null,
  naCount: '',
  naNextStageNo: null,
  skipCount: '',
  skipMaxPeriod: '',
  skipMaxPeriodUnit: DEFAULT_PERIOD_UNIT,
  skipNextStageNo: null,
  ...overrides,
});

export const emptyRule = () => ({
  id: null,
  name: '',
  description: '',
  resetAfterPeriod: '',
  resetAfterPeriodUnit: DEFAULT_PERIOD_UNIT,
  stages: [makeStage({ stageNo: '1', name: 'Ask', isInitial: true })],
});

export const RULE_PRESETS = [
  {
    value: 'ask-once',
    label: 'Ask once, then skip for a while',
    build: () => ({
      name: 'Ask once, then skip 3 inspections',
      description:
        'Asked until it passes once, then skipped for the next 3 inspections or 3 months.',
      stages: [
        makeStage({ stageNo: '1', name: 'Ask', isInitial: true, okCount: '1', okNextStageNo: 2, failCount: '1', failNextStageNo: 1 }),
        makeStage({ stageNo: '2', name: 'Skip', kind: STAGE_KIND.SKIP, skipCount: '3', skipMaxPeriod: '3', skipMaxPeriodUnit: 'month', skipNextStageNo: 1 }),
      ],
    }),
  },
  {
    value: 'streak',
    label: 'Pass several times in a row, then skip',
    build: () => ({
      name: '5 passes in a row, then skip 3 inspections',
      description:
        'Has to pass 5 inspections in a row; a fail restarts the count. Then skipped for 3 inspections or 3 months.',
      stages: [
        makeStage({ stageNo: '1', name: 'Ask', isInitial: true, okCount: '5', okNextStageNo: 2, failCount: '1', failNextStageNo: 1 }),
        makeStage({ stageNo: '2', name: 'Skip', kind: STAGE_KIND.SKIP, skipCount: '3', skipMaxPeriod: '3', skipMaxPeriodUnit: 'month', skipNextStageNo: 1 }),
      ],
    }),
  },
  {
    value: 'skip-after-na',
    label: 'Skip for good after an N/A answer',
    build: () => ({
      name: 'Skip after an N/A answer',
      description:
        'Asked until it gets an N/A answer, then skipped for the next 999 inspections or 99 years.',
      stages: [
        makeStage({ stageNo: '1', name: 'Ask', isInitial: true, naCount: '1', naNextStageNo: 2 }),
        makeStage({ stageNo: '2', name: 'Skip', kind: STAGE_KIND.SKIP, skipCount: '999', skipMaxPeriod: '99', skipMaxPeriodUnit: 'year', skipNextStageNo: 1 }),
      ],
    }),
  },
  {
    value: 'ladder',
    label: 'Normal, tightened and reduced AQL',
    build: () => ({
      name: 'Tighten on a fail, reduce after 5 passes',
      description:
        'A fail on normal tightens the AQL; 5 passes in a row on tightened go back to normal; 5 passes in a row on normal reduce it; a fail on reduced goes back to normal.',
      stages: [
        makeStage({ stageNo: '10', name: 'Normal', isInitial: true, okCount: '5', okNextStageNo: 30, failCount: '1', failNextStageNo: 20 }),
        makeStage({ stageNo: '20', name: 'Tightened', levelShift: 1, defectShift: 1, okCount: '5', okNextStageNo: 10, failCount: '1', failNextStageNo: 20 }),
        makeStage({ stageNo: '30', name: 'Reduced', levelShift: -1, defectShift: -1, failCount: '1', failNextStageNo: 10 }),
      ],
    }),
  },
];

const blankToString = (value) => (value === null || value === undefined ? '' : `${value}`);
const toNumberOrNull = (value) =>
  value === '' || value === null || value === undefined ? null : Number(value);

// Fields typed into number inputs: strings in the form, numbers or null in the API.
const TYPED_NUMBER_FIELDS = ['stageNo', 'okCount', 'failCount', 'naCount', 'skipCount', 'skipMaxPeriod'];
// Fields picked from a stage list: numbers or null in both.
export const PICKED_STAGE_FIELDS = ['okNextStageNo', 'failNextStageNo', 'naNextStageNo', 'skipNextStageNo'];

const mapFields = (stage, fields, convert) =>
  fields.reduce((all, field) => ({ ...all, [field]: convert(stage[field]) }), {});

export const ruleToForm = (rule) => ({
  id: rule.id,
  name: rule.name || '',
  description: rule.description || '',
  resetAfterPeriod: blankToString(rule.resetAfterPeriod),
  resetAfterPeriodUnit: rule.resetAfterPeriodUnit || DEFAULT_PERIOD_UNIT,
  stages: (rule.stages || []).map((stage) =>
    makeStage({
      ...stage,
      ...mapFields(stage, TYPED_NUMBER_FIELDS, blankToString),
      skipMaxPeriodUnit: stage.skipMaxPeriodUnit || DEFAULT_PERIOD_UNIT,
    }),
  ),
});

export const formToApi = (form) => ({
  name: form.name.trim(),
  description: form.description.trim() || null,
  resetAfterPeriod: toNumberOrNull(form.resetAfterPeriod),
  resetAfterPeriodUnit: form.resetAfterPeriodUnit,
  stages: form.stages.map((stage) => ({
    name: stage.name.trim(),
    kind: stage.kind,
    isInitial: !!stage.isInitial,
    levelShift: Number(stage.levelShift || 0),
    defectShift: Number(stage.defectShift || 0),
    skipMaxPeriodUnit: stage.skipMaxPeriodUnit,
    ...mapFields(stage, [...TYPED_NUMBER_FIELDS, ...PICKED_STAGE_FIELDS], toNumberOrNull),
  })),
});

/** Checks the form can be sent; the API checks how the stages connect. */
export const validateRuleForm = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = ['Give the rule a name'];
  if (!form.stages.length) errors.stages = ['Add at least one stage'];
  form.stages.forEach((stage, index) => {
    if (!stage.stageNo) errors[`stages.${index}.stageNo`] = ['Required'];
    if (!stage.name.trim()) errors[`stages.${index}.name`] = ['Required'];
  });
  return errors;
};
